import React, { useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router';
import { t } from 'ttag';
import * as Yup from 'yup';

// Import unified components
import {
  AuthLayout,
  AuthForm,
  AuthField,
  AuthLink,
  AuthMessage
} from '../UnifiedComponents';

// Import existing components
import assetsService from '@services/assetsService';
import { passwordRuleMessage, PASSWORD_REGEX } from '@services/formService';
import { useStoreActions } from '@models/RootStore';

// Validation schema - dynamically create based on whether token is in URL
const createValidationSchema = (hasToken: boolean) => {
  const baseSchema = {
    password: Yup.string()
      .matches(PASSWORD_REGEX, passwordRuleMessage)
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
  };

  if (!hasToken) {
    return Yup.object().shape({
      ...baseSchema,
      token: Yup.string().required('Token is required'),
    });
  }

  return Yup.object().shape(baseSchema);
};

interface ResetPasswordProps {
  theme?: 'light' | 'dark';
}

const UnifiedResetPassword: React.FC<ResetPasswordProps> = ({
  theme = 'light'
}) => {
  // Get history hook
  const history = useHistory();
  // Get token from URL params
  const match = useRouteMatch<{ token: string }>();
  const urlToken: string = match?.params?.token || '';
  const hasToken = !!urlToken;

  // Store actions
  const resetPassword = useStoreActions((action) => action.users.resetPassword);

  // Component state
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form initial values - dynamically create based on whether token is in URL
  const initialFormValues = hasToken
    ? {
        password: '',
        confirmPassword: '',
      }
    : {
        token: '',
        password: '',
        confirmPassword: '',
      };

  /**
   * Handles navigation back to login page
   */
  const handleNavigateToLogin = () => {
    history.push('/login');
  };

  /**
   * Handles password reset form submission
   * @param formData - Form data containing token and passwords
   */
  const handlePasswordResetSubmission = async (formData: { password: string; confirmPassword: string; token?: string }) => {
    const token = formData.token || urlToken;

    if (!formData.password || !formData.confirmPassword || !token) {
      setFormError('Invalid token or missing password');
      return;
    }

    setIsSubmittingForm(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      // Call the actual reset password API
      await resetPassword({ token, password: formData.password });

      // Clear form
      formData.password = '';
      formData.confirmPassword = '';

      // Redirect to login after a delay
      history.push('/reset-success');

    } catch (error: any) {
      setFormError(error.message || 'An error occurred while resetting your password');
    } finally {
      setIsSubmittingForm(false);
    }
  };


  return (
    <AuthLayout
      theme={theme}
      title={t`Reset Password`}
      subtitle={t`Enter your new password below`}
      logo={assetsService.getResourcePath('logo.png')}
      onLogoError={() => {
        // Handle logo error silently
      }}
    >
      {/* Success Message */}
      {successMessage && (
        <AuthMessage
          theme={theme}
          type="success"
          message={successMessage}
        />
      )}

      {/* Reset Password Form */}
      <AuthForm
        theme={theme}
        initialValues={initialFormValues}
        validationSchema={createValidationSchema(hasToken)}
        onSubmit={handlePasswordResetSubmission}
        submitButtonText={isSubmittingForm ? t`Resetting...` : t`Reset Password`}
        isSubmitting={isSubmittingForm}
      >
        {/* Token Field - only show if no token in URL */}
        {!hasToken && (
          <AuthField
            theme={theme}
            label={t`Reset Token`}
            name="token"
            type="text"
            required
          />
        )}

        {/* Password Field */}
        <AuthField
          theme={theme}
          label={t`New Password`}
          name="password"
          type="password"
          required
        />

        {/* Confirm Password Field */}
        <AuthField
          theme={theme}
          label={t`Confirm New Password`}
          name="confirmPassword"
          type="password"
          required
        />

        {/* Error Display */}
        {formError && (
          <AuthMessage
            theme={theme}
            type="error"
            message={formError}
          />
        )}
      </AuthForm>

      {/* Back to Login Link */}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <AuthLink
          theme={theme}
          onClick={handleNavigateToLogin}
          ariaLabel={t`Navigate back to login page`}
        >
          {t`Back to Login`}
        </AuthLink>
      </div>
    </AuthLayout>
  );
};

export default UnifiedResetPassword;
